import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { toast } from "sonner";

import ApiService from "../services/api-service";
import { isNativeApp, isPushConfigured } from "./native";
import { enableWebPush, getWebPushStatus, initWebPush } from "./webPush";

// Fired on window when the user taps a push notification (or the in-app
// banner's "View"); AppContent navigates there, asking to sign in first when
// the notification was for an account (personal).
export const PUSH_OPEN_PATH_EVENT = "inspirelens:pushOpenPath";
export type PushOpenPathDetail = { path: string; personal: boolean; notificationId?: number };

// Fired on window whenever notifications may have changed (a push arrived,
// one was read/deleted), so the bell badge refreshes.
export const NOTIFICATIONS_CHANGED_EVENT = "inspirelens:notificationsChanged";

// needs-install: an iPhone browser tab; push needs the site on the Home Screen.
export type PushStatus = "unsupported" | "unconfigured" | "needs-install" | "prompt" | "granted" | "denied";

const OFFER_DISMISSED_KEY = "inspirelens-push-offer-dismissed";
const DEVICE_ID_KEY = "inspirelens-device-id";

let deviceToken: string | null = null;
let initialized = false;
let listenersAdded = false;

const readStorage = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const writeStorage = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage blocked: the value just isn't remembered.
  }
};

// A random id for this install. When FCM rotates the token, the backend
// replaces this install's old token instead of keeping both.
function getDeviceId(): string {
  let id = readStorage(DEVICE_ID_KEY);
  if (!id) {
    id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    writeStorage(DEVICE_ID_KEY, id);
  }
  return id;
}

// iOS hands out a raw APNs token (64 hex characters) unless Firebase Messaging
// is set up in the iOS project (see AppDelegate.swift); FCM can't send to it.
const isFcmToken = (token: string) => !/^[0-9a-f]{64}$/i.test(token);

// In-app paths only ("/post/quote/1"), never another site ("//evil.com").
const safePath = (value: unknown): string | null =>
  typeof value === "string" && value.startsWith("/") && !value.startsWith("//") ? value : null;

// Tells the backend about this install, linked to whoever is signed in now (the
// API reads the stored session token), so notifications reach the right person
// and signed-out installs still get the daily quote.
async function registerDevice(): Promise<void> {
  if (!deviceToken) return;
  try {
    await ApiService.registerPushDevice({
      token: deviceToken,
      platform: Capacitor.getPlatform() as "android" | "ios",
      deviceId: getDeviceId(),
    });
  } catch (error) {
    console.warn("Push device registration failed:", error);
  }
}

function openFromNotification(data: Record<string, unknown> | undefined): void {
  const path = safePath(data?.deepLink) ?? safePath(data?.url);
  if (!path) return;
  const notificationId = Number(data?.notificationId) || undefined;
  const detail: PushOpenPathDetail = { path, personal: Boolean(notificationId), notificationId };
  window.dispatchEvent(new CustomEvent(PUSH_OPEN_PATH_EVENT, { detail }));
}

// Added once per app run, however often push is (re)enabled.
async function addListeners(): Promise<void> {
  if (listenersAdded) return;
  listenersAdded = true;

  await PushNotifications.addListener("registration", ({ value }) => {
    if (!isFcmToken(value)) {
      console.warn("Push: got an APNs token, not an FCM token. Set up Firebase Messaging in the iOS project.");
      return;
    }
    deviceToken = value;
    registerDevice();
  });
  await PushNotifications.addListener("registrationError", (error) => {
    console.warn("Push registration failed:", error?.error ?? error);
  });
  // The app is open: Android shows nothing on its own, so show an in-app
  // banner. iOS already shows the system banner (presentationOptions in
  // capacitor.config.ts), so only the badge needs refreshing there.
  await PushNotifications.addListener("pushNotificationReceived", (notification) => {
    window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT));
    if (Capacitor.getPlatform() !== "android") return;
    const title = notification.title || "The Deepam";
    toast(title, {
      description: notification.body,
      duration: 5000,
      action: safePath(notification.data?.deepLink ?? notification.data?.url)
        ? { label: "View", onClick: () => openFromNotification(notification.data) }
        : undefined,
    });
  });
  await PushNotifications.addListener("pushNotificationActionPerformed", ({ notification }) => {
    openFromNotification(notification.data);
  });
  // Sign-in/out relinks this install to the right account (or none).
  window.addEventListener("inspirelens:userchange", () => {
    registerDevice();
  });
}

async function createChannels(): Promise<void> {
  if (Capacitor.getPlatform() !== "android") return;
  const channels = [
    { id: "social", name: "Followers and activity", description: "New followers, likes, comments, replies and mentions", importance: 4 as const },
    { id: "new_content", name: "Posts from people you follow", description: "When someone you follow shares something new", importance: 4 as const },
    { id: "default", name: "Daily quote", description: "One quote a day", importance: 3 as const },
  ];
  await Promise.all(channels.map((channel) => PushNotifications.createChannel(channel).catch(() => {})));
}

export async function getPushStatus(): Promise<PushStatus> {
  if (!isNativeApp) return getWebPushStatus();
  if (!(await isPushConfigured())) return "unconfigured";
  const permission = await PushNotifications.checkPermissions();
  if (permission.receive === "granted") return "granted";
  if (permission.receive === "denied") return "denied";
  return "prompt";
}

// Asks for permission (the system prompt appears once; Android 13+ and iOS)
// and registers this install. Call from a button the user tapped, after
// explaining what notifications are for.
export async function enablePushNotifications(): Promise<PushStatus> {
  if (!isNativeApp) {
    const webStatus = await enableWebPush();
    if (webStatus !== "needs-install") writeStorage(OFFER_DISMISSED_KEY, "1");
    return webStatus;
  }
  let status = await getPushStatus();
  if (status === "unsupported" || status === "unconfigured" || status === "denied") return status;
  await addListeners();
  if (status === "prompt") {
    const permission = await PushNotifications.requestPermissions();
    status = permission.receive === "granted" ? "granted" : "denied";
    writeStorage(OFFER_DISMISSED_KEY, "1");
  }
  if (status === "granted") await PushNotifications.register();
  return status;
}

// Whether to show the "turn on notifications" card: permission not asked yet
// (or, on an iPhone, the site isn't on the Home Screen) and the user hasn't
// said "not now".
export async function shouldOfferPush(): Promise<boolean> {
  if (readStorage(OFFER_DISMISSED_KEY)) return false;
  const status = await getPushStatus();
  return status === "prompt" || status === "needs-install";
}

export function isPushOfferDismissed(): boolean {
  return Boolean(readStorage(OFFER_DISMISSED_KEY));
}

export function dismissPushOffer(): void {
  writeStorage(OFFER_DISMISSED_KEY, "1");
}

// App start: sets up channels and listeners and, if the user already allowed
// notifications, registers this install. Never shows the permission prompt
// itself - that waits for enablePushNotifications(). On the website this is
// web push (lib/webPush.ts); does nothing where Firebase isn't configured.
export async function initPushNotifications(): Promise<void> {
  if (!isNativeApp) return initWebPush();
  if (initialized) return;
  initialized = true;
  if (!(await isPushConfigured())) return;

  await createChannels();
  await addListeners();
  if ((await getPushStatus()) === "granted") {
    await PushNotifications.register();
  }
}
